import { client } from '../common/client';
import { uploadReviewMediaFiles } from './utils/mediaUpload';
import type {
  CreateReviewMediaPresignedUrlRequest,
  CreateReviewMediaPresignedUrlResponse,
  CreateReviewRequest,
  CreateReviewResponse,
  GetCuratedReviewsRequest,
  GetCuratedReviewsResponse,
  GetAIProductReviewsRequest,
  GetAIProductReviewsResponse,
  GetMyReviewsRequest,
  GetMyReviewsResponse,
  GetProductReviewsRequest,
  GetProductReviewsResponse,
  GetReviewCreditAmountsRequest,
  GetReviewCreditAmountsResponse,
  GetReviewCreditEligibilityRequest,
  GetReviewCreditEligibilityResponse,
  GetReviewCreditSummaryRequest,
  GetReviewCreditSummaryResponse,
  ReviewStatisticsRequest,
  ReviewStatisticsResponse,
  UpdateReviewRequest,
  UpdateReviewResponse,
} from './utils/types';
import { ReviewStatus } from '../../constants';

class ReviewAPIClient {
  public async getProductReviews(
    productId: number,
    params?: Omit<GetProductReviewsRequest, 'productId'>,
  ): Promise<GetProductReviewsResponse> {
    return await client.get(`/reviews/product/${productId}`, { params }).then((res) => res.data);
  }

  public async getCuratedReviews(params?: GetCuratedReviewsRequest): Promise<GetCuratedReviewsResponse> {
    return await client.get('/reviews/curated', { params }).then((res) => res.data);
  }

  public async getAIProductReviews(
    productId: number,
    params?: Omit<GetAIProductReviewsRequest, 'productId'>,
  ): Promise<GetAIProductReviewsResponse> {
    return await client
      .get(`/reviews/product/${productId}`, {
        params: {
          ...params,
          tags: 'AIReview',
          status: ReviewStatus.Submitted,
        },
      })
      .then((res) => res.data);
  }

  public async getMyReviews(params?: GetMyReviewsRequest): Promise<GetMyReviewsResponse> {
    return await client.get('/reviews/me', { params }).then((res) => res.data);
  }

  public async getReviewCreditAmounts(params?: GetReviewCreditAmountsRequest): Promise<GetReviewCreditAmountsResponse> {
    return await client.get('/reviews/creditAmounts', { params }).then((res) => res.data);
  }

  public async getReviewCreditEligibility({
    productId,
  }: GetReviewCreditEligibilityRequest): Promise<GetReviewCreditEligibilityResponse> {
    return await client.get(`/reviews/eligibility/products/${productId}`).then((res) => res.data);
  }

  public async getReviewCreditSummary(params?: GetReviewCreditSummaryRequest): Promise<GetReviewCreditSummaryResponse> {
    return await client.get('/reviews/creditSummary', { params }).then((res) => res.data);
  }

  public async getReviewStatistics(params: ReviewStatisticsRequest): Promise<ReviewStatisticsResponse> {
    return await client.get('/reviews/statistics', { params }).then((res) => res.data);
  }

  public async createReviewMediaPresignedUrl(
    params: CreateReviewMediaPresignedUrlRequest,
  ): Promise<CreateReviewMediaPresignedUrlResponse> {
    return await client.post('/reviews/media/presignedUrl', params).then((res) => res.data);
  }

  public async createReview(params: CreateReviewRequest): Promise<CreateReviewResponse> {
    const { files, mediaUrls, ...review } = params;
    const uploadedUrls = await uploadReviewMediaFiles(
      files,
      (uploadParams) => this.createReviewMediaPresignedUrl(uploadParams),
      mediaUrls?.length ?? 0,
    );

    return await client
      .post('/reviews', {
        ...review,
        mediaUrls: [...(mediaUrls ?? []), ...uploadedUrls],
      })
      .then((res) => res.data);
  }

  public async updateReview(params: UpdateReviewRequest): Promise<UpdateReviewResponse> {
    const { reviewId, productId, files, mediaUrls, ...review } = params;
    // Only `keepMediaUrls` states the whole retained set. `keepPictureUrls` governs pictures alone,
    // leaving every video kept but uncounted, and sending neither keeps everything — in both cases
    // the client cannot total the attachments, so the cap is the server's alone to enforce.
    const retainedCount = review.keepMediaUrls?.length ?? 0;
    const uploadedUrls = await uploadReviewMediaFiles(
      files,
      (uploadParams) => this.createReviewMediaPresignedUrl(uploadParams),
      retainedCount + (mediaUrls?.length ?? 0),
    );
    const appendedUrls = [...(mediaUrls ?? []), ...uploadedUrls];

    return await client
      .put(
        `/reviews/${reviewId}`,
        {
          ...review,
          // An empty list is indistinguishable from none; retention is decided by `review`.
          ...(appendedUrls.length > 0 ? { mediaUrls: appendedUrls } : {}),
        },
        { params: { productId } },
      )
      .then((res) => res.data);
  }
}

export const reviewAPIClient = new ReviewAPIClient();
